import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Modal } from 'react-bootstrap';
import swal from 'sweetalert';
import { PageHeader, Card, Button, Badge, Table, Tr, Td, EmptyState, Field, Input, Select } from '../../ui';

const API = (process.env.REACT_APP_API_URL || '/api') + '/v1';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('streamToken') || ''}` });

const ApiKeys = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', scopes: 'read' });
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/keys`, { headers: authHeaders() });
      setKeys(data.data || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/keys`, form, { headers: authHeaders() });
      setNewKey(data.data);
      setShowCreate(false);
      setForm({ name: '', scopes: 'read' });
      load();
    } catch (e) {
      swal('Error', e.response?.data?.error?.message || 'Failed to create key', 'error');
    } finally { setCreating(false); }
  };

  const revoke = (k) => {
    swal({
      title: 'Revoke this key?',
      text: `"${k.name}" will stop working immediately for every app using it.`,
      icon: 'warning', buttons: ['Cancel', 'Revoke'], dangerMode: true,
    }).then(async (ok) => {
      if (!ok) return;
      try {
        await axios.delete(`${API}/keys/${k.id}`, { headers: authHeaders() });
        load();
      } catch { swal('Error', 'Failed to revoke key', 'error'); }
    });
  };

  const copy = (t) => navigator.clipboard.writeText(t).then(() => swal('Copied!', 'API key copied to clipboard.', 'success', { timer: 1400, buttons: false }));

  return (
    <>
      <PageHeader
        title="API Keys"
        subtitle="Authenticate your website and mobile apps against the Teve API"
        actions={
          <>
            <Button variant="secondary" icon="fa-book" as="a" href={`${API}/docs`} target="_blank">
              API Docs
            </Button>
            <Button icon="fa-plus" onClick={() => setShowCreate(true)}>New Key</Button>
          </>
        }
      />

      <Card className="tw-mb-6" bodyClass="tw-text-sm tw-text-content-muted">
        <div className="tw-flex tw-flex-wrap tw-gap-x-8 tw-gap-y-2">
          <div><span className="tw-font-semibold tw-text-heading">Base URL</span> &nbsp;<code className="tw-rounded tw-bg-sunken tw-px-1.5 tw-py-0.5">{window.location.origin}/api/v1</code></div>
          <div><span className="tw-font-semibold tw-text-heading">Auth</span> &nbsp;<code className="tw-rounded tw-bg-sunken tw-px-1.5 tw-py-0.5">Authorization: Bearer tev_live_…</code></div>
          <div><span className="tw-font-semibold tw-text-heading">Scopes</span> &nbsp;read, write</div>
        </div>
      </Card>

      <Card noPad>
        {loading ? (
          <div className="tw-py-10 tw-text-center"><i className="fa-solid fa-spinner fa-spin tw-text-brand" /></div>
        ) : keys.length === 0 ? (
          <EmptyState
            icon="fa-key"
            title="No API keys yet"
            hint="Create a key to start calling the Teve API from your own apps."
            action={<Button icon="fa-plus" onClick={() => setShowCreate(true)}>Create API Key</Button>}
          />
        ) : (
          <Table head={['Name', 'Key', 'Scopes', 'Last used', 'Requests', '']}>
            {keys.map((k) => (
              <Tr key={k.id}>
                <Td className="tw-font-semibold tw-text-heading">{k.name}</Td>
                <Td><code className="tw-rounded tw-bg-sunken tw-px-1.5 tw-py-0.5 tw-text-xs">{k.key_prefix}…</code></Td>
                <Td>{String(k.scopes).split(',').map((s) => <Badge key={s} tone={s === 'write' ? 'warn' : 'neutral'} className="tw-mr-1">{s}</Badge>)}</Td>
                <Td className="tw-text-content-muted">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : 'never'}</Td>
                <Td>{k.request_count || 0}</Td>
                <Td className="tw-text-right">
                  {k.revoked_at
                    ? <Badge tone="live">revoked</Badge>
                    : <Button variant="ghost" size="sm" icon="fa-trash" onClick={() => revoke(k)}>Revoke</Button>}
                </Td>
              </Tr>
            ))}
          </Table>
        )}
      </Card>

      {/* Create modal */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
        <Modal.Header closeButton><Modal.Title>New API key</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="tw-space-y-4">
            <Field label="Name" hint="So you can tell your keys apart (e.g. “Marketing site”, “iOS app”).">
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="My app" />
            </Field>
            <Field label="Scope">
              <Select value={form.scopes} onChange={(e) => setForm((f) => ({ ...f, scopes: e.target.value }))}>
                <option value="read">read — list & fetch only</option>
                <option value="read,write">read + write — create/update/delete streams</option>
              </Select>
            </Field>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button onClick={create} disabled={creating}>{creating ? 'Creating…' : 'Create Key'}</Button>
        </Modal.Footer>
      </Modal>

      {/* Show-once modal */}
      <Modal show={!!newKey} onHide={() => setNewKey(null)} centered>
        <Modal.Header closeButton><Modal.Title>Your new API key</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="tw-rounded-lg tw-border tw-border-warn/30 tw-bg-warn/10 tw-px-3 tw-py-2 tw-text-sm tw-text-warn">
            Copy it now — it won't be shown again.
          </div>
          <div className="tw-mt-3 tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-line tw-bg-sunken tw-p-3">
            <code className="tw-flex-1 tw-break-all tw-text-sm">{newKey?.key}</code>
            <Button size="sm" icon="fa-copy" onClick={() => copy(newKey.key)}>Copy</Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setNewKey(null)}>Done</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ApiKeys;
